import React, { FC, useState, useEffect } from "react";
import { DocumentData, Query, QuerySnapshot, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { state } from "../../../index";
import * as customersActions from "../../../store/actions/customersActions";
import * as userActions from "../../../store/actions/usersActions";
import authState from "../../../store/reducers/authState";
import { Customer, Employee } from "../../auth/Auth";
import { EmployeeErrors, validateForm } from "./validationUtils";
import Input from "../../UI/Input";
import Select from "../../UI/Select";
import Button from "../../UI/Button";
import { Modal, ModalClose, ModalDialog, Typography } from "@mui/joy";
import { FormControl, SelectChangeEvent } from "@mui/material";
import { Save } from "@mui/icons-material";
import { toast } from "react-toastify";
import customersState from "../../../store/reducers/customersState";

interface UserModalProps {
    open: boolean;
    selectedUser: Employee | null;
    onHandleClose: () => void;
}

const UserModal: FC<UserModalProps> = ({ open, selectedUser, onHandleClose }) => {
    const [employeeData, setEmployeeData] = useState<Employee>({
        employeeId: "",
        customerId: "",
        email: "",
        fullName: "",
        role: ""
    });
    const [errors, setErrors] = useState<EmployeeErrors>({
        fullName: "",
        email: "",
        role: ""
    });

    const dispatch = useDispatch();
    const { fetchCustomers } = bindActionCreators(customersActions, dispatch);
    const { addEmployee } = bindActionCreators(userActions, dispatch);

    const authState: authState = useSelector((state: state) => state.auth);
    const customersState: customersState = useSelector((state: state) => state.customers);
    const userRole: string = authState.user.role;
    const customers: Customer[] = customersState.customers;

    useEffect(() => {
        if (userRole === "admin") {
            fetchCustomers();
        }
    }, [userRole]);

    useEffect(() => {
        if (!selectedUser) {
            //add mode
            setEmployeeData({
                employeeId: "",
                customerId: authState.user.customerId,
                email: "",
                fullName: "",
                role: ""
            });
        } else {
            //edit mode
            setEmployeeData(selectedUser);
        }
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        setEmployeeData({ ...employeeData, [name]: value });
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const handleSelectChange = (e: SelectChangeEvent): void => {
        const { name, value } = e.target;
        setEmployeeData({ ...employeeData, [name]: value });
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const checkIfEmailExists = async (email: string, customerId: string): Promise<boolean> => {
        const usersQuery: Query<DocumentData> = query(collection(db, "customers", customerId, "employees"), where("email", "==", email));
        const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(usersQuery);
        return !querySnapshot.empty;
    }

    const handleAddOrEditUser = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        const { valid, errors } = validateForm(employeeData);
        const isAddMode = selectedUser === null;

        if (!valid) {
            setErrors(errors);
        } else if (isAddMode && await checkIfEmailExists(employeeData.email, employeeData.customerId)) {
            toast.error("Unešena email adresa je prethodno uneta.");
        } else {
            addEmployee(employeeData, isAddMode);
            onHandleClose();
        }
    };

    return (
        <Modal open={open} onClose={onHandleClose}>
            <ModalDialog
                aria-labelledby="basic-modal-dialog-title"
                aria-describedby="basic-modal-dialog-description"
                sx={{ minWidth: { sm: "500px" }, gap: 0 }}
            >
                <ModalClose variant="outlined" />
                <Typography id="basic-modal-dialog-title" level="h2">
                    {!selectedUser ? "Dodaj novog korisnika" : "Izmeni korisnika"}
                </Typography>
                {userRole === "admin" &&
                    <Select
                        label="Kompanija"
                        value={employeeData.customerId}
                        options={customers.map(customer => ({ key: customer.id, value: customer.name }))}
                        disabled={selectedUser !== null}
                        name="customerId"
                        onSelectChange={handleSelectChange}
                    />}
                <Input
                    label="Ime i prezime"
                    type="text"
                    value={employeeData.fullName}
                    name="fullName"
                    required
                    error={errors.fullName}
                    onChange={handleInputChange}
                />
                <Input
                    label="Email"
                    type="text"
                    value={employeeData.email}
                    name="email"
                    disabled={selectedUser !== null}
                    required
                    error={errors.email}
                    onChange={handleInputChange}
                />
                <Select
                    label="Uloga"
                    value={employeeData.role}
                    options={[
                        ...(userRole === "admin" ? [{ key: "admin", value: "Administrator" }] : []),
                        { key: "customerAdmin", value: "Korisnik (Admin)" },
                        { key: "customerUser", value: "Korisnik" }
                    ]}
                    name="role"
                    required
                    error={errors.role}
                    onSelectChange={handleSelectChange}
                />
                <FormControl sx={{ my: 1.5 }}>
                    <Button
                        label="Sačuvaj"
                        startIcon={<Save />}
                        className="btn_success"
                        handleClick={handleAddOrEditUser}
                    />
                </FormControl>
            </ModalDialog>
        </Modal>
    );
};

export default UserModal;